import MarkdownIt from 'markdown-it';
import { full as emoji } from 'markdown-it-emoji';
import type Quill from 'quill';
import Turndown from 'turndown';

type Renderer = typeof MarkdownIt.prototype.renderer;
type Token = typeof MarkdownIt.prototype.Token;
type Options = typeof MarkdownIt.prototype.options;

const md = MarkdownIt({
  breaks: true,
  html: false,
  linkify: true,
  typographer: true,
})
  .use(emoji)
  .use((md: MarkdownIt) => {
    function renderLink(
      tokens: Token[],
      idx: number,
      opts: Options,
      env: unknown,
      self: Renderer,
    ): string {
      tokens.forEach((token) => {
        if (token.type === 'link_open') {
          token.attrSet('target', '_blank');
          token.attrSet('rel', 'noopener noreferrer');

          const href = token.attrGet('href');

          if (href) {
            token.attrSet('title', href);
            token.attrSet('v-link-guard', '');
          }
        }
      });

      return self.renderToken(tokens, idx, opts);
    }

    function renderImage(tokens: Token[], idx: number): string {
      return md.utils.escapeHtml(tokens[idx].content);
    }

    // eslint-disable-next-line dot-notation
    md.renderer.rules['link_open'] = renderLink;
    md.renderer.rules.image = renderImage;
  });

const turndown = new Turndown();
const escapeText = turndown.escape.bind(turndown);

// Allow Markdown typed inside formatted Quill text to nest; unsafe round-trips use raw editing.
turndown.escape = value => escapeText(value).replace(/\\_([^_\n]+)\\_/g, '_$1_');

turndown.addRule('strikethrough', {
  filter: ['del', 's'],
  replacement(content) {
    return `~~${content}~~`;
  },
});

turndown.addRule('script', {
  filter: ['script', 'pre'],
  replacement(content) {
    return `\`\`\`\n${content}\n\`\`\``;
  },
});

export function renderMessageMarkdown(value: string): string {
  return md.render(value);
}

export function renderMessagePreview(value: string): string {
  return md.renderInline(value);
}

export function loadMessageForEditing(editor: Quill, value: string): boolean {
  const unsupportedTokens = new Set([
    'blockquote_open',
    'code_block',
    'code_inline',
    'fence',
    'hr',
    'image',
    'table_open',
  ]);
  const hasUnsupportedFormatting = md.parse(value, {}).some(token =>
    unsupportedTokens.has(token.type)
    || token.children?.some(child => unsupportedTokens.has(child.type)),
  );

  if (hasUnsupportedFormatting) {
    editor.setText(value);
    return true;
  }

  editor.setContents(editor.clipboard.convert({
    html: renderMessageMarkdown(value).replace(/<br>\n/g, '<br>'),
  }));

  if (serializeMessageEditor(editor.getSemanticHTML(), editor.getText()) !== value) {
    editor.setText(value);

    return true;
  }

  return false;
}

export function serializeMessageEditor(
  html: string,
  text: string,
  isPlainMarkdown = false,
  originalMarkdown?: string,
): string {
  if (isPlainMarkdown) {
    return originalMarkdown !== undefined && !originalMarkdown.endsWith('\n')
      ? text.replace(/\n$/, '')
      : text;
  }

  const stripped = html.replace(/<\/?p>/g, '');
  const hasEmptyParagraph = /<p>(?:<br\s*\/?>)?<\/p>/i.test(html);

  if (detectFormat(stripped) === 'Markdown') {
    if (hasEmptyParagraph && md.parse(text, {}).every(token =>
      ['paragraph_open', 'inline', 'paragraph_close'].includes(token.type),
    )) {
      const lines = text.split('\n');

      while (lines.at(-1)?.trim() === '') {
        lines.pop();
      }

      return lines.join('\n')
        .replace(/\n{2,}/g, breaks => '\\\n'.repeat(breaks.length));
    }

    return text;
  }

  const cleanedHtml = html.replace(/&nbsp;/g, ' ');

  if (hasEmptyParagraph) {
    const container = document.createElement('div');
    container.innerHTML = cleanedHtml;

    const blocks = Array.from(container.childNodes, (node) => {
      if (node instanceof HTMLElement) {
        return turndown.turndown(node);
      }
      if (node instanceof Text) {
        return node.data;
      }
      throw new Error('Unexpected editor content while serializing Markdown.');
    });

    while (blocks.at(-1)?.trim() === '') {
      blocks.pop();
    }

    return blocks.join('\\\n');
  }

  return turndown.turndown(cleanedHtml);
}

function detectFormat(input: string): 'Markdown' | 'HTML' | 'Both' | 'Unknown' {
  const markdownPatterns = [
    /^#{1,6}\s/m, // Headers
    /(\*\*|__)(.*?)\1/, // Bold
    /(\*|_)(.*?)\1/, // Italic
    /~~(.*?)~~/, // Strikethrough
    /!\[.*?\]\(.*?\)/, // Images
    /\[.*?\]\(.*?\)/, // Links
    /^>\s/m, // Blockquotes
    /^([-+*])\s/m, // Unordered lists
    /^\d+\.\s/m, // Ordered lists
    /`([^`]+)`/, // Inline code
    /^```[\s\S]*?```$/m, // Code blocks
  ];

  const htmlPatterns = [
    /<!DOCTYPE html>/i, // Doctype
    /<html[\s\S]*?>/i, // HTML tag
    /<head[\s\S]*?>/i, // Head tag
    /<body[\s\S]*?>/i, // Body tag
    /<\/?[a-z][\s\S]*?>/i, // Any HTML tag
    /&[a-z]+;/i, // HTML entities
  ];

  const isMarkdown = markdownPatterns.some(pattern => pattern.test(input));
  const isHTML = htmlPatterns.some(pattern => pattern.test(input));

  if (isMarkdown && !isHTML) {
    return 'Markdown';
  }
  else if (isHTML && !isMarkdown) {
    return 'HTML';
  }
  else if (isMarkdown && isHTML) {
    return 'Both';
  }
  else {
    return 'Unknown';
  }
}
